import { gql } from "@apollo/client"
import styled from "@emotion/styled"
import { mq, theme } from "@pallet-hq/hegel"
import ApplyButton from "components/Buttons/ApplyButton"
import ApproveDenyButtons from "components/Buttons/ApproveDeny"
import CompanyPreview from "components/CompanyPreview"
import { useRouter } from "next/router"
import ListingDetails from "page-components/pallet/ListingLayout/ListingDetails"
import { JobPostEditorModalProvider } from "state/useJobPostEditorModal"
import { PostStatusEnum } from "__generated__/globalTypes"
import { ListingInfo_listing } from "__generated__/ListingInfo_listing"
import { PostInfo_post } from "__generated__/PostInfo_post"
import { useScreenSize } from "utils/hooks/useSize"
import PostBackButton from "./PostBackButton"
import PostDescription from "./PostDescription"
import PosterDetails from "./PosterDetails"
import PostHeader from "./PostHeader"
import PromptAnswer from "./PromptAnswer"

export const ListingInfoCompatibilityFragment = gql`
    fragment ListingInfoCompatibilityFragment_listing on ListingType {
        id
        uuid
        isReviewable
        promptAnswer
        ...PromptAnswer_listingtype
        applylist {
            id
            slug
            canEdit
        }
        ...ListingDetails_listing
    }
    ${PromptAnswer.fragment}
    ${ListingDetails.fragment}
`

export const PostInfoFragment = gql`
    fragment PostInfo_post on PostType {
        ...PostHeader_post
        company {
            ...CompanyPreview_company
        }
        ...PostDescription_post
        ...ApproveDeny_post
        ...ApplyButton_post
        ...PosterDetails_post
        canEdit
    }
    ${ApplyButton.fragment}
    ${ApproveDenyButtons.fragment}
    ${PostHeader.fragment}
    ${CompanyPreview.fragment}
    ${PostDescription.fragment}
    ${PosterDetails.fragment}
`

export type PostInfoProps = {
    post: PostInfo_post
    listing?: ListingInfo_listing
}

const PostInfo = (props: PostInfoProps) => {
    const { isMobile } = useScreenSize()
    const router = useRouter()
    const { uuid } = router.query
    const slug = props.listing?.applylist.slug
    const showApproveReject = props.listing?.isReviewable

    return (
        <JobPostEditorModalProvider>
            <PostInfoDiv>
                {slug && uuid && <PostBackButton {...props} />}
                <PostHeader post={props.post} />
                {props.listing?.applylist.canEdit &&
                    (props.listing?.id ? (
                        <ListingDetails listing={props.listing} />
                    ) : (
                        <PosterDetails post={props.post} />
                    ))}
                <ApplyRow>
                    {props.post.status === PostStatusEnum.CONFIRMED && (
                        <ApplyButton
                            post={props.post}
                            listing={props.listing}
                        />
                    )}
                    {showApproveReject && (
                        <ApproveDenyButtons post={props.post} />
                    )}
                </ApplyRow>
                {props.listing && props.listing.promptAnswer && (
                    <PromptAnswer listing={props.listing} />
                )}
                {isMobile && (
                    <CompanyPreviewWrapper>
                        <CompanyPreview company={props.post.company} />
                    </CompanyPreviewWrapper>
                )}
                <PostDescription post={props.post} />
            </PostInfoDiv>
        </JobPostEditorModalProvider>
    )
}

const paddingSizes: string[] = [theme.space.small, theme.space.large]
const PostInfoDiv = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
    box-sizing: border-box;
    ${mq({ padding: paddingSizes.map(p => `${p} ${p} ${p} ${p}`) })}
    ${mq({ paddingTop: [theme.space.medium, theme.space.large] })}
`

const CompanyPreviewWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    ${mq({ margin: [`${theme.space.medium} -16px 0px`, "0px"] })}
`

const ApplyRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: ${theme.space.xsmall} 0px 0px;

    & > * {
        margin-right: ${theme.space.xsmall};
        flex-wrap: nowrap;

        > button {
            margin-right: ${theme.space.xsmall};
            margin-bottom: 0;
        }
    }
`

PostInfo.fragment = PostInfoFragment

export default PostInfo
